import {
  USERS_ACCESS_ACCOUNT_STATES,
  USERS_ACCESS_ACCOUNT_TYPES,
  USERS_ACCESS_ROLES,
  type UsersAccessFilterCountsI,
  type UsersAccessFilterValuesI,
  type UsersAccessPendingInviteRowI,
  type UsersAccessRole,
  type UsersAccessRow,
  type UsersAccessTechnicianRowI,
  type UsersAccessUserRowI,
} from '../types/usersAccess'

import type {
  ManagedAccountsQuery,
  PendingInvitesQuery,
  TechniciansQuery,
} from '@/graphql/generated/operations'

import { DEFAULT_ROLE } from '../types/types'

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

const ROLE_SET = new Set<string>(USERS_ACCESS_ROLES)

const normalizeRole = (value: string): UsersAccessRole | null => {
  if (!value) return null
  if (value === DEFAULT_ROLE) return 'OPERATOR'

  const upper = value.toUpperCase()
  const normalized = upper.startsWith('ROLE_')
    ? upper.replace('ROLE_', '')
    : upper

  return ROLE_SET.has(normalized) ? (normalized as UsersAccessRole) : null
}

export const normalizeUsersAccessRoles = (
  roles: string[] | undefined,
  roleName?: string | null
): UsersAccessRole[] => {
  const normalizedRoles = (roles ?? [])
    .map(normalizeRole)
    .filter((role): role is UsersAccessRole => role !== null)
  if (normalizedRoles.length > 0) {
    return Array.from(new Set(normalizedRoles))
  }

  const normalizedRoleName = roleName ? normalizeRole(roleName) : null
  return normalizedRoleName ? [normalizedRoleName] : ['EMPLOYEE']
}

export const isUsersAccessInviteExpired = (
  invitedAt: string,
  lastInviteSentAt: string | null,
  now = Date.now()
): boolean => {
  const referenceDate = lastInviteSentAt || invitedAt
  return now > new Date(referenceDate).getTime() + TWENTY_FOUR_HOURS_MS
}

export const getNextUsersAccessInviteExpiration = (
  pendingInvitesData: PendingInvitesQuery | undefined,
  now = Date.now()
): number | null => {
  const futureExpirations =
    pendingInvitesData?.pendingInvites
      .map((invite) => {
        const referenceDate = invite.lastInviteSentAt ?? invite.createdAt
        return new Date(referenceDate).getTime() + TWENTY_FOUR_HOURS_MS
      })
      .filter(
        (expiration) => Number.isFinite(expiration) && expiration > now
      ) ?? []

  return futureExpirations.length > 0 ? Math.min(...futureExpirations) : null
}

const getFullName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | null | undefined
) =>
  [firstName, lastName].filter(Boolean).join(' ').trim() || email?.trim() || ''

export const buildUsersAccessRows = (
  usersData: ManagedAccountsQuery | undefined,
  techniciansData: TechniciansQuery | undefined,
  pendingInvitesData: PendingInvitesQuery | undefined,
  now = Date.now()
): UsersAccessRow[] => {
  // An invited user is created enabled in Keycloak, so `managedAccounts` returns
  // them as well as `pendingInvites` (same id) until they sign in. Keep the invite
  // row, since that is where resend/revoke live, and drop the duplicate user row.
  const pendingInviteIds = new Set(
    pendingInvitesData?.pendingInvites.map((invite) => invite.id)
  )

  const users: UsersAccessUserRowI[] =
    usersData?.managedAccounts?.edges
      ?.flatMap((edge) => (edge?.node ? [edge.node] : []))
      .filter((user) => !pendingInviteIds.has(user.id))
      .map((user) => ({
        kind: 'user',
        key: `user:${user.id}`,
        id: user.id,
        email: user.email ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        fullName: getFullName(user.firstName, user.lastName, user.email),
        roles: normalizeUsersAccessRoles(user.roles, user.roleName),
        roleName: user.roleName ?? null,
        accountState: 'ACTIVE',
        accountType: 'USER',
      })) ?? []

  const technicians: UsersAccessTechnicianRowI[] =
    techniciansData?.technicians?.edges
      ?.flatMap((edge) => (edge?.node ? [edge.node] : []))
      .map((technician) => ({
        kind: 'technician',
        key: `technician:${technician.id}`,
        id: technician.id,
        email: technician.email ?? '',
        firstName: technician.firstName ?? '',
        lastName: technician.lastName ?? '',
        fullName: getFullName(
          technician.firstName,
          technician.lastName,
          technician.email
        ),
        roles: ['TECHNICIAN'],
        accountState: null,
        accountType: 'TECHNICIAN',
        deviceTypes: technician.deviceTypes ?? [],
      })) ?? []

  const pendingInvites: UsersAccessPendingInviteRowI[] =
    pendingInvitesData?.pendingInvites.map((invite) => ({
      kind: 'pending-invite',
      key: `pending-invite:${invite.id}`,
      id: invite.id,
      email: invite.email ?? '',
      firstName: invite.firstName ?? '',
      lastName: invite.lastName ?? '',
      fullName: getFullName(invite.firstName, invite.lastName, invite.email),
      roles: normalizeUsersAccessRoles(invite.roles),
      accountState: isUsersAccessInviteExpired(
        invite.createdAt,
        invite.lastInviteSentAt ?? null,
        now
      )
        ? 'INVITE_EXPIRED'
        : 'INVITED',
      accountType: 'PENDING_INVITE',
      invitedAt: invite.createdAt,
      lastInviteSentAt: invite.lastInviteSentAt ?? null,
    })) ?? []

  return [...users, ...technicians, ...pendingInvites].sort((a, b) => {
    const lastNameComparison = a.lastName.localeCompare(b.lastName)
    if (lastNameComparison !== 0) return lastNameComparison

    const firstNameComparison = a.firstName.localeCompare(b.firstName)
    if (firstNameComparison !== 0) return firstNameComparison

    return a.email.localeCompare(b.email)
  })
}

export const filterUsersAccessRows = (
  rows: UsersAccessRow[],
  search: string,
  filters: UsersAccessFilterValuesI
): UsersAccessRow[] => {
  const normalizedSearch = search.trim().toLocaleLowerCase()

  return rows.filter((row) => {
    const isSearchMatch =
      normalizedSearch === '' ||
      row.fullName.toLocaleLowerCase().includes(normalizedSearch) ||
      row.email.toLocaleLowerCase().includes(normalizedSearch)
    const isRoleMatch =
      filters.roles.length === 0 ||
      row.roles.some((role) => filters.roles.includes(role))
    const isStateMatch =
      filters.accountStates.length === 0 ||
      (row.accountState !== null &&
        filters.accountStates.includes(row.accountState))
    const isTypeMatch =
      filters.accountTypes.length === 0 ||
      filters.accountTypes.includes(row.accountType)

    return isSearchMatch && isRoleMatch && isStateMatch && isTypeMatch
  })
}

export const canManageUsersAccessRow = (
  row: UsersAccessRow,
  hasCRUDPermission: (role: UsersAccessRole) => boolean
): boolean => row.roles.every(hasCRUDPermission)

export const countUsersAccessFilters = (
  rows: UsersAccessRow[]
): UsersAccessFilterCountsI => {
  const counts: UsersAccessFilterCountsI = {
    roles: Object.fromEntries(
      USERS_ACCESS_ROLES.map((role) => [role, 0])
    ) as Record<UsersAccessRole, number>,
    accountStates: Object.fromEntries(
      USERS_ACCESS_ACCOUNT_STATES.map((state) => [state, 0])
    ) as UsersAccessFilterCountsI['accountStates'],
    accountTypes: Object.fromEntries(
      USERS_ACCESS_ACCOUNT_TYPES.map((type) => [type, 0])
    ) as UsersAccessFilterCountsI['accountTypes'],
  }

  rows.forEach((row) => {
    row.roles.forEach((role) => {
      counts.roles[role] += 1
    })
    if (row.accountState) counts.accountStates[row.accountState] += 1
    counts.accountTypes[row.accountType] += 1
  })

  return counts
}

export const getUsersAccessFilterCount = (
  filters: UsersAccessFilterValuesI
): number =>
  filters.roles.length +
  filters.accountStates.length +
  filters.accountTypes.length
